import React, { useState, useEffect } from "react";
import {
  Stack,
  Title,
  Text,
  Button,
  Grid,
  Alert,
  LoadingOverlay,
  Paper,
  Group,
} from "@mantine/core";
import { IconCoins, IconPlus, IconAlertCircle } from "@tabler/icons-react";
import { useNavigate } from "react-router-dom";
import { useSalon } from "../../context/SalonContext";
import { useApp } from "../../context/AppContext";
import { useOnboarding } from "../../context/OnboardingContext";
import usePermissions from "../../hooks/usePermissions";
import { normalizeDepositConfigsResponse } from "../../utils/depositConfigUtils";
import { DepositConfiguration } from "../../types/deposit";
import { BusinessInfo, CurrentUser, SalonService } from "../../types/context";
import DepositCard from "./DepositCard";
import DepositForm from "./DepositForm";

const DepositSettings = () => {
  const navigate = useNavigate();
  const salonContext = useSalon() as any;
  const { businessInfo, currentUser, services } = salonContext;
  const perms = usePermissions();
  const depositsPerms = perms?.settingsDeposits;
  const {
    getDepositConfigs,
    createDepositConfig,
    updateDepositConfig,
    deleteDepositConfig,
  } = useApp();
  const { setDepositConfigCount } = useOnboarding();

  const [deposits, setDeposits] = useState<DepositConfiguration[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isCreating, setIsCreating] = useState(false);
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (businessInfo?.id) {
      fetchDeposits();
    }
  }, [businessInfo?.id]);

  const fetchDeposits = async () => {
    try {
      setIsLoading(true);
      const response = await getDepositConfigs(businessInfo.id);
      const list = normalizeDepositConfigsResponse(
        response,
      ) as DepositConfiguration[];
      setDeposits(list);
      setDepositConfigCount(list.length);
    } catch (error) {
      console.error("Error fetching deposits:", error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleCreateDeposit = async (formData: any) => {
    try {
      setIsCreating(true);
      setError(null);
      const response = await createDepositConfig({
        ...formData,
        salon_id: businessInfo.id,
        is_active: true,
      });

      if (response) {
        setDeposits((prev) => [...prev, response]);
        setDepositConfigCount((prev) => (prev !== null ? prev + 1 : 1));
        setShowCreateForm(false);
      }
    } catch (error: any) {
      console.error("Error creating deposit:", error);

      // Handle collision detection error
      if (
        error.message &&
        error.message.includes(
          "Collision detected with existing active configurations:",
        )
      ) {
        const cleanMessage = error.message.replace(" (Status Code: 400)", "");
        setError(cleanMessage);
      } else {
        setError("Failed to create deposit configuration. Please try again.");
      }
    } finally {
      setIsCreating(false);
    }
  };

  const handleUpdateDeposit = async (formData: any) => {
    try {
      setIsCreating(true);
      setError(null);
      const response = await updateDepositConfig({
        ...formData,
        salon_id: businessInfo.id,
      });

      if (response) {
        setDeposits((prev) =>
          prev.map((deposit) =>
            deposit.id === formData.id ? { ...deposit, ...response } : deposit,
          ),
        );
      }
    } catch (error: any) {
      console.error("Error updating deposit:", error);

      // Handle collision detection error
      if (
        error.message &&
        error.message.includes(
          "Collision detected with existing active configurations:",
        )
      ) {
        const cleanMessage = error.message.replace(" (Status Code: 400)", "");
        setError(cleanMessage);
      } else {
        setError("Failed to update deposit configuration. Please try again.");
      }
    } finally {
      setIsCreating(false);
    }
  };

  const handleDeleteDeposit = async (depositId: string) => {
    try {
      setIsCreating(true);
      await deleteDepositConfig(businessInfo.id, depositId);
      setDeposits((prev) => prev.filter((deposit) => deposit.id !== depositId));
      setDepositConfigCount((prev) =>
        prev !== null && prev > 0 ? prev - 1 : 0,
      );
    } catch (error) {
      console.error("Error deleting deposit:", error);
    } finally {
      setIsCreating(false);
    }
  };

  const handleNavigateToPayments = () => {
    navigate("/admin/settings/payments");
  };

  const clearError = () => {
    setError(null);
  };

  // Use section_permissions when available; otherwise fall back to role + Stripe
  // Use explicit === true check to fail-closed when permissions are undefined
  const hasAccessByRole =
    currentUser?.role === "Owner" ||
    (currentUser?.role === "Manager" &&
      businessInfo?.stripe_connect_account_is_onboarded);
  const hasAccess =
    depositsPerms?.canAccess === true ||
    (depositsPerms === undefined && hasAccessByRole);

  if (!hasAccess) {
    return (
      <Stack p="md">
        <Title order={3} fw={500}>
          Deposits
        </Title>
        <Alert
          icon={<IconAlertCircle size={16} />}
          title="Access Restricted"
          color="yellow"
        >
          Deposits are only available to Owners and Managers with completed
          Stripe onboarding.
        </Alert>
      </Stack>
    );
  }

  // Check Stripe integration status
  if (!businessInfo?.stripe_connect_account_id) {
    return (
      <Stack p="md">
        <Title order={3} fw={500}>
          Deposits
        </Title>
        <Alert
          icon={<IconAlertCircle size={16} />}
          title="Stripe Account Required"
          color="blue"
        >
          To enable deposits, please visit Payments settings to onboard your
          Stripe account.
        </Alert>
        <Button
          onClick={handleNavigateToPayments}
          color="teal"
          leftSection={<IconCoins size={16} />}
        >
          Go to Payments Settings
        </Button>
      </Stack>
    );
  }

  if (
    businessInfo?.stripe_connect_account_id &&
    !businessInfo?.stripe_connect_account_is_onboarded
  ) {
    return (
      <Stack p="md">
        <Title order={3} fw={500}>
          Deposits
        </Title>
        <Alert
          icon={<IconAlertCircle size={16} />}
          title="Complete Stripe Onboarding"
          color="blue"
        >
          To enable deposits, finish onboarding Stripe by completing your
          account setup.
        </Alert>
        <Button
          onClick={handleNavigateToPayments}
          color="teal"
          leftSection={<IconCoins size={16} />}
        >
          Complete Stripe Setup
        </Button>
      </Stack>
    );
  }

  return (
    <Stack p="md">
      <LoadingOverlay visible={isLoading} />

      {error && (
        <Alert
          icon={<IconAlertCircle size={16} />}
          title="Error"
          color="red"
          withCloseButton
          onClose={clearError}
        >
          {error}
        </Alert>
      )}

      <Group justify="space-between" align="center">
        <div>
          <Title order={3} fw={500}>
            Deposits
          </Title>
          <Text size="sm" c="dimmed">
            Configure deposit requirements for your services. Deposits can not
            exceed the amount of any service.
          </Text>
        </div>

        {!showCreateForm && (
          <Button
            onClick={() => setShowCreateForm(true)}
            leftSection={<IconPlus size={16} />}
            color="teal"
          >
            Add Deposit Configuration
          </Button>
        )}
      </Group>

      {showCreateForm && (
        <Paper p="md" withBorder>
          <DepositForm
            services={services || []}
            onSubmit={handleCreateDeposit}
            onCancel={() => {
              setShowCreateForm(false);
              clearError();
            }}
            isLoading={isCreating}
          />
        </Paper>
      )}

      {deposits.length === 0 && !showCreateForm ? (
        <Paper p="xl" withBorder>
          <Stack align="center" gap="md">
            <IconCoins size={48} color="gray" />
            <Text size="lg" fw={500}>
              No Deposit Configurations Found
            </Text>
            <Text size="sm" c="dimmed" ta="center">
              Create one to start enforcing deposits for your services now!
            </Text>
            <Button
              onClick={() => setShowCreateForm(true)}
              leftSection={<IconPlus size={16} />}
              color="teal"
            >
              Create First Deposit
            </Button>
          </Stack>
        </Paper>
      ) : (
        <Grid>
          {deposits.map((deposit) => {
            return (
              <Grid.Col key={deposit?.id} span={{ base: 12, sm: 6, lg: 4 }}>
                <DepositCard
                  deposit={deposit}
                  services={services || []}
                  onUpdate={handleUpdateDeposit}
                  onDelete={handleDeleteDeposit}
                  isLoading={isCreating}
                />
              </Grid.Col>
            );
          })}
        </Grid>
      )}
    </Stack>
  );
};

export default DepositSettings;
